/**
 * API client for admin Global-e migration and portal actions (migrate, remigrate, activate, open RMA export, CSV upload).
 * Includes `postDownload` for JSON POSTs whose response is read as `blob` (file vs JSON message), matching former HttpService behavior.
 */
import axios, { AxiosResponse } from "axios";
import { injectable } from "inversify";
import errorModalStore from "../../components/error-modal/error-modal-store";
import { TYPE, rootContainer } from "../container/root";
import { isSet } from "../../models/model-types-methods";
import { IApiResponse, IHttpService, generateContextId } from "../http-service";
import {
    IMigrateGlobalEMerchantsRequestData
} from "../../pages/admins/migrate-global-e-merchants/stores/migrate-global-e-merchants-page-store";

export enum GlobalEEnvironment {
    QA = "QA",
    INT = "INT",
    STG = "STG",
    PROD = "PRODUCTION",
}

export interface IMigrateGlobaleShopifyStoreRequest {
    portal_id: number;
}

export interface IRemigrateGlobalEMerchantRequest {
    portal_id: number;
    merchantId: string;
    environment: GlobalEEnvironment;
}

export interface IMigrateGlobaleShopifyStoreResponse {
    num_deleted_reasons: number;
    num_deleted_resolutions: number;
    num_deleted_return_methods: number;
}

export interface IGlobaleStoreMetaFields {
    merchant_id: number;
    env: GlobalEEnvironment;
}

export interface ILastCompletedShopMigrationAttempt {
    migration_finished_at: string | null;
    status: "completed" | "completed_with_errors";
}

export interface IActivateGlobaleMerchantResponse {
    successful_activation: boolean;
}

export type DownloadOpenRmasResult =
    | { kind: "file"; blob: Blob; filename: string }
    | { kind: "message"; message: string };

@injectable()
class AdminMigrateGlobalEMerchantsService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    public async migrateGlobalEMerchants(request: IMigrateGlobalEMerchantsRequestData): Promise<void> {
        await this.httpService.post<IApiResponse<void>>(
            "/rms-api/admin-migrate-global-e-merchants",
            {
              ...request
            }
        );
    }

    public async migrateGlobalEShopifyPlatformStore(request: IMigrateGlobaleShopifyStoreRequest): Promise<IMigrateGlobaleShopifyStoreResponse> {
        const res = await this.httpService.post<IApiResponse<IMigrateGlobaleShopifyStoreResponse>>(
            "/rms-api/admin-migrate-shopify-platform-globale-merchants",
            {
              ...request
            }
        );
        return res.payload;
    }

    /** POST /rms-api/admin-remigrate-global-e-merchant — re-run Global-E platform migration for an existing portal. */
    public async remigrateGlobalEMerchant(request: IRemigrateGlobalEMerchantRequest): Promise<void> {
        await this.httpService.post<IApiResponse<{ success: boolean }>>(
            "/rms-api/admin-remigrate-global-e-merchant",
            {
                portal_id: request.portal_id,
                merchantId: request.merchantId,
                environment: request.environment,
            }
        );
    }
    
    public async fetchGlobaleStoreMetaFields(portalId: number): Promise<IGlobaleStoreMetaFields> {
        const res = await this.httpService.get<IApiResponse<IGlobaleStoreMetaFields>>(
            `/rms-api/admin-migrate-shopify-get-globale-metafields/${portalId}`
        );
        return res.payload;
    }

    public async fetchLastCompletedShopMigrationAttempt(
        portalId: number
    ): Promise<ILastCompletedShopMigrationAttempt | null> {
        const res = await this.httpService.get<
            IApiResponse<{ last_completed_attempt: ILastCompletedShopMigrationAttempt | null }>
        >(`/rms-api/admin-migrate-last-completed-migration-attempt/${portalId}`);
        return res.payload.last_completed_attempt;
    }

    public async activateGlobaleMerchant(request: IMigrateGlobaleShopifyStoreRequest): Promise<IActivateGlobaleMerchantResponse> {
        const res = await this.httpService.post<IApiResponse<IActivateGlobaleMerchantResponse>>(
            '/rms-api/admin-activate-globale-merchant',
            request
        );
        return res.payload;
    }

    /**
     * POST /rms-api/admin-download-open-rmas — CSV file or JSON body with `message` when no export is ready.
     */
    public async downloadOpenRmas(portalId: number): Promise<DownloadOpenRmasResult> {
        const response = await this.postDownload("/rms-api/admin-download-open-rmas", {
            portal_id: portalId,
        });

        const contentType = String(response.headers["content-type"] ?? "");
        if (contentType.includes("application/json")) {
            const text = await (response.data as Blob).text();
            const json = JSON.parse(text) as { message?: string };
            return { kind: "message", message: String(json.message ?? "") };
        }

        const filename = `${portalId}_open-rmas.csv`;
        return { kind: "file", blob: response.data as Blob, filename };
    }

    /**
     * POST /rms-api/admin-upload-open-rmas-csv — multipart CSV upload; queues import job.
     */
    public async uploadOpenRmasCsv(portalId: number, file: File): Promise<{ message: string }> {
        const formData = new FormData();
        formData.append("portal_id", String(portalId));
        formData.append("file", file);
        const baseUrl = this.httpService.getBaseUrl() ?? "";
        const requestId = generateContextId();

        try {
            const res = await axios.post<{ message: string }>(
                `${baseUrl}/rms-api/admin-upload-open-rmas-csv`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        Accept: "application/json",
                        "X-Request-Id": requestId,
                    },
                }
            );
            return res.data;
        }
        catch (error: any) {
            const responseData = error?.response?.data?.errors;
            this.showResponseErrorInModal(responseData, responseData?.code || error?.response?.status, error.message);

            throw error;
        }
    }

    /**
     * POST with JSON body; response is read as `blob` and returned with headers (e.g. file download vs JSON message).
     * Mirrors the former `HttpService` POST + `responseType: 'blob'` path without delegating to that class.
     */
    private async postDownload(path: string, data?: unknown): Promise<AxiosResponse<Blob>> {
        const baseUrl = this.httpService.getBaseUrl() ?? "";
        const headers: Record<string, string> = {
            Accept: "*/*",
            "Content-Type": "application/json",
            "X-Request-Id": generateContextId(),
        };
        try {
            return await axios<Blob>(`${baseUrl}${path}`, {
                method: "POST",
                headers,
                data: data !== undefined && data !== null ? JSON.stringify(data) : undefined,
                responseType: "blob",
                withCredentials: true,
            });
        }
        catch (error: unknown) {
            const err = error as { response?: AxiosResponse; request?: unknown; message?: string };
            const responseData = await (err.response ? this.parseBlobResponseIntoJSON(err.response) : undefined);

            this.showResponseErrorInModal(responseData?.errors, responseData?.code || err?.response?.status, err.message);

            throw error;
        }
    }

    private showResponseErrorInModal(responseData?: Record<string, any>, responseStatusCode?: number, errorMessage?: string): void {

        if (isSet(responseData)) {
            console.error("response", responseData);
            const code = responseData?.code || responseStatusCode || 500;
            const validationErrors = responseData?.errors;

            if (validationErrors) {
                for (const i in validationErrors) {
                    errorModalStore.add({
                        title: validationErrors[i][0] ?? "Server Error",
                        code,
                    });
                }
            }
            else {
                errorModalStore.add({
                    ...responseData,
                    title: JSON.stringify(responseData) ?? "Server Error",
                    code,
                });
            }
        }
        else {
            errorModalStore.add({
                title: errorMessage ?? "Error",
            });
        }
    }

    /**
     * When `responseType` is `blob`, Axios exposes JSON bodies as a `Blob`.
     * Uses `Content-Type` and text-then-JSON.parse pattern to get response data.
     */
    private async parseBlobResponseIntoJSON(response: AxiosResponse): Promise<Record<string, any>> {
        const { data } = response;
        if (!(data instanceof Blob)) {
            return data as Record<string, any>;
        }
        const contentType = String(response.headers["content-type"] ?? "");
        const text = await data.text();
        if (!text.trim()) {
            return {};
        }
        const isJsonContentType = contentType.includes("application/json");
        if (!isJsonContentType) {
            return {};
        }
        try {
            return JSON.parse(text) as Record<string, any>;
        } catch {
            return {};
        }
    }
}

function formatHttpErrorEndpoint(error: { config?: { method?: string; url?: string } } | unknown): string {
    const e = error as { config?: { method?: string; url?: string } };
    return [String(e.config?.method ?? "").toUpperCase(), e.config?.url].filter(Boolean).join(" ");
}

export default AdminMigrateGlobalEMerchantsService;
